<template>
  <div class="system-roles-page page">
    <div class="page-header">
      <div class="header-actions">
        <el-radio-group
          v-model="query.groupId"
          @change="fetchData"
          size="medium"
          class="group-radio-group"
        >
          <el-radio-button :label="1">{{ $t('systemRole.domestic') }}</el-radio-button>
          <el-radio-button :label="2">{{ $t('systemRole.international') }}</el-radio-button>
        </el-radio-group>
        <el-button type="primary" @click="openCreate">
          <i class="el-icon-plus mr-1"></i>
          {{ $t('systemRole.addRole') }}
        </el-button>
      </div>
    </div>

    <div class="overflow-y-auto pl-2 pb-2 flex-1" v-loading="loading" style="min-height: 0;">
      <div class="grid grid-cols-3 gap-7">
        <div
          v-for="role in roles"
          :key="role.id"
          class="bg-gray-700 rounded-xl text-white p-4"
        >
        <div class="w-full pt-2 pb-2 bg-gray-700 rounded-xl">
          <!-- 卡片头部 -->
          <div class="w-full pl-5 pr-5 pt-2 pb-2 flex justify-between text-white text-lg bg-purple-600 rounded-xl">
            <div class="flex items-center gap-4">
              <el-avatar
                :src="role.avatarPath"
                icon="el-icon-user"
                size="large"
                shape="circle"
                class="bg-white text-gray-700"
              ></el-avatar>
              <div class="flex flex-col items-start">
                <div>{{ role.name || $t('systemRole.unnamedRole') }}</div>
                <div>{{ $t('common.id') }}：{{ role.id }}</div>
              </div>
            </div>
            <div class="flex items-center">
              <div v-if="role.isDefault" style="color: #38EB0C">{{ $t('common.default') }}</div>
              <i class="el-icon-delete ml-6 cursor-pointer" @click="onDelete(role)"></i>
            </div>
          </div>

          <!-- 3D 模型预览（有模型文件时） -->
          <div
            class="mt-2 w-full model-preview-container"
            style="height: 250px; position: relative;"
            v-if="role.modelPath"
          >
            <!-- 已点击“加载预览”后，才真正渲染 3D 模型 -->
            <template v-if="isRoleModelLoaded(role.id)">
              <!-- FBX 预览 -->
              <FbxModel
                v-if="getModelFormat(role) === 'fbx'"
                :model-path="role.modelPath"
                :camera-distance="1.5"
                :enable-controls="true"
                :scale="{ x: 20, y: 20, z: 20 }"
                :position="{ x: 0, y: -2100, z: 0 }"
                :rotation="{ x: 0, y: 0, z: 0 }"
                background="#374151"
                @progress="onLoadProgress"
                @error="onLoadError"
              />
              <!-- VRM 预览 -->
              <VrmModel
                v-else-if="getModelFormat(role) === 'vrm'"
                :model-path="role.modelPath"
                :camera-distance="2"
                :enable-controls="true"
                :scale="{ x: 1, y: 1, z: 1 }"
                :position="{ x: 0, y: 0, z: 0 }"
                :rotation="{ x: 0, y: 0, z: 0 }"
                background="#374151"
                @progress="onLoadProgress"
                @error="onLoadError"
              />
              <!-- 其它格式占位 -->
              <div
                v-else
                class="w-full h-full flex items-center justify-center bg-gray-600 rounded-lg"
              >
                {{ getModelFormat(role) }} {{ $t('systemRole.modelPreviewNotSupported') }}
              </div>
            </template>
            <!-- 默认占位：不加载模型，只显示“加载预览”按钮 -->
            <template v-else>
              <div
                class="w-full h-full flex flex-col items-center justify-center bg-gray-600 rounded-lg"
              >
                <i class="el-icon-view text-3xl text-gray-300 mb-2"></i>
                <div class="text-gray-300 mb-2">
                  {{ $t('systemRole.systemRoleSettings') }}
                </div>
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  @click="loadRoleModelPreview(role.id)"
                >
                  {{ $t('systemRole.enableModelPreview') }}
                </el-button>
              </div>
            </template>
            <!-- 重新上传按钮 - 右上角 -->
            <div class="reupload-button-container">
              <el-upload
                :action="uploadModelAction"
                :show-file-list="false"
                :on-success="(res) => handleModelUploadSuccess(role, res)"
                :on-error="(err) => handleModelUploadError(role, err)"
                :on-progress="(event) => handleModelUploadProgress(role, event)"
                :before-upload="(file) => beforeModelUpload(role, file)"
                :headers="uploadHeaders"
                :data="{ roleId: role.id }"
                :auto-upload="true"
                accept=".fbx,.vrm"
              >
                <el-button size="mini" type="primary" plain :loading="getModelUploading(role.id)">
                  <i class="el-icon-upload2 mr-1"></i>
                  {{ $t('systemRole.reuploadModel') }}
                </el-button>
              </el-upload>
              <el-progress
                v-if="getModelUploading(role.id)"
                :percentage="getModelUploadProgress(role.id)"
                :status="getModelUploadProgress(role.id) === 100 ? 'success' : null"
                :stroke-width="4"
                style="width: 120px; margin-top: 4px;"
              ></el-progress>
            </div>
          </div>
          <!-- 无模型时的占位 -->
          <div class="mt-2 w-full flex items-center justify-center bg-gray-600 rounded-lg" style="height: 250px" v-else>
            <div class="text-center">
              <i class="el-icon-upload2 text-4xl text-gray-400 mb-2"></i>
              <div class="text-gray-400">{{ $t('systemRole.noModelUploaded') }}</div>
              <div class="upload-model-container">
                <el-upload
                  :action="uploadModelAction"
                  :show-file-list="false"
                  :on-success="(res) => handleModelUploadSuccess(role, res)"
                  :on-error="(err) => handleModelUploadError(role, err)"
                  :on-progress="(event) => handleModelUploadProgress(role, event)"
                  :before-upload="(file) => beforeModelUpload(role, file)"
                  :headers="uploadHeaders"
                  :data="{ roleId: role.id }"
                  :auto-upload="true"
                  accept=".fbx,.vrm"
                  class="mt-2"
                >
                  <el-button size="mini" type="primary" plain :loading="getModelUploading(role.id)">
                    <i class="el-icon-upload2 mr-1"></i>
                    {{ $t('systemRole.uploadModel') }}
                  </el-button>
                </el-upload>
                <el-progress
                  v-if="getModelUploading(role.id)"
                  :percentage="getModelUploadProgress(role.id)"
                  :status="getModelUploadProgress(role.id) === 100 ? 'success' : null"
                  :stroke-width="6"
                  style="width: 200px; margin-top: 8px;"
                ></el-progress>
              </div>
            </div>
          </div>

          <!-- 属性信息 -->
          <div class="flex justify-between gap-x-5 mt-3">
            <div class="text-sm text-white p-2 pb-2 pl-4 w-1/2 bg-gray-500 rounded-xl">
              <div class="text-gray-400">{{ $t('systemRole.language') }}</div>
              <div>{{ role.language || '—' }}</div>
            </div>
            <div class="text-sm text-white pt-2 pb-2 pl-4 w-1/2 bg-gray-500 rounded-xl">
              <div class="text-gray-400">{{ $t('systemRole.speechRate') }}</div>
              <div>{{ formatNumber(role.speechRate) }}</div>
            </div>
          </div>
          <div class="flex justify-between gap-x-5 mt-3">
            <div class="text-sm text-white p-2 pb-2 pl-4 w-1/2 bg-gray-500 rounded-xl">
              <div class="text-gray-400">{{ $t('systemRole.pitchRate') }}</div>
              <div>{{ formatNumber(role.pitchRate) }}</div>
            </div>
            <div class="text-sm text-white pt-2 pb-2 pl-4 w-1/2 bg-gray-500 rounded-xl">
              <div class="text-gray-400">{{ $t('systemRole.volume') }}</div>
              <div>{{ role.volume ?? '—' }}</div>
            </div>
          </div>
          <div class="flex justify-between gap-x-5 mt-3">
            <div class="text-sm text-white p-2 pb-2 pl-4 w-full bg-gray-500 rounded-xl">
              <div class="text-gray-400">{{ $t('systemRole.voice') }}</div>
              <div>{{ role.voice || $t('systemRole.defaultVoice') }}</div>
            </div>
          </div>

          <!-- 背景提示词 -->
          <div class="text-lg text-white mt-5">{{ $t('systemRole.characterBackground') }}</div>
          <div class="text-sm text-gray-500 mt-2">
            <el-tooltip
              v-if="role.prompt"
              effect="dark"
              placement="top"
              :content="role.prompt"
              popper-class="prompt-tooltip"
            >
              <div class="line-clamp">
                {{ role.prompt }}
              </div>
            </el-tooltip>
            <div v-else class="line-clamp">
              {{ $t('systemRole.noPrompt') }}
            </div>
          </div>

          <!-- 分隔线 -->
          <div class="my-4 border-t border-gray-600 ml-3 mr-3"></div>

          <!-- 操作按钮 -->
          <div class="mt-3 flex gap-5 mr-10 items-center justify-center h-18">
            <el-button type="primary"
                       class="border border-purple-600 bg-transparent hover:bg-transparent h-15 w-1/6 rounded-xl"
                       @click="openEdit(role)">
              {{ $t('systemRole.edit') }}
            </el-button>
            <el-button
              v-if="!role.isDefault"
              type="primary"
              class="bg-purple-600 hover:bg-purple-700 border-none h-15 px-4 rounded-xl whitespace-nowrap"
              @click="onSetDefault(role)">
              {{ $t('systemRole.isDefault') }}
            </el-button>
          </div>
        </div>
      </div>
      </div>
      <div v-if="!loading && roles.length === 0" class="flex items-center justify-center h-full text-gray-400">
        {{ $t('systemRole.noSystemRoles') }}
      </div>
    </div>


    <el-dialog :title="dialogTitle" :visible.sync="dialog.visible" width="600px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item :label="$t('systemRole.avatar')" prop="avatarPath">
          <div class="avatar-upload-container">
            <el-upload
              class="avatar-uploader"
              :action="uploadAction"
              :show-file-list="false"
              :on-success="handleAvatarSuccess"
              :before-upload="beforeAvatarUpload"
              :headers="uploadHeaders"
              :auto-upload="true"
            >
              <div v-if="hasAvatar" class="avatar-preview">
                <img :src="form.avatarPath" class="avatar" @error="handleImageError" />
                <div class="avatar-overlay">
                  <i class="el-icon-edit"></i>
                </div>
              </div>
              <div v-else class="avatar-placeholder">
                <i class="el-icon-plus avatar-uploader-icon"></i>
                <div class="upload-text">{{ $t('systemRole.clickToUpload') }}</div>
              </div>
            </el-upload>
            <div class="avatar-tips">
              <p>{{ $t('systemRole.clickToUploadAvatar') }}</p>
              <p>{{ $t('systemRole.avatarUploadTip') }}</p>
            </div>
          </div>
        </el-form-item>
        <el-form-item :label="$t('systemRole.name')" prop="name">
          <el-input v-model="form.name"></el-input>
        </el-form-item>
        <el-form-item :label="$t('systemRole.prompt')" prop="prompt">
          <el-input type="textarea" :rows="3" v-model="form.prompt"></el-input>
        </el-form-item>
        <el-form-item :label="$t('systemRole.speechRate')" prop="speechRate">
          <el-input-number v-model="form.speechRate" :min="0" :max="5" :step="0.1" controls-position="right" />
        </el-form-item>
        <el-form-item :label="$t('systemRole.pitchRate')" prop="pitchRate">
          <el-input-number v-model="form.pitchRate" :min="0" :max="5" :step="0.1" controls-position="right" />
        </el-form-item>
        <el-form-item :label="$t('systemRole.volume')" prop="volume">
          <el-input-number v-model="form.volume" :min="0" :max="100" :step="1" controls-position="right" />
        </el-form-item>
        <el-form-item :label="$t('systemRole.voice')" prop="voice">
          <el-input v-model="form.voice"></el-input>
        </el-form-item>
        <el-form-item :label="$t('systemRole.language')" prop="language">
          <el-input v-model="form.language"></el-input>
        </el-form-item>
        <el-form-item :label="$t('systemRole.selectGroup')" prop="groupId">
          <el-select v-model="form.groupId" style="width: 100%;">
            <el-option :label="$t('systemRole.domestic')" :value="1"></el-option>
            <el-option :label="$t('systemRole.international')" :value="2"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialog.visible=false">{{ $t('systemRole.cancel') }}</el-button>
        <el-button type="primary" @click="onSubmit">{{ $t('systemRole.save') }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/network/systemRole/index'
import FbxModel from '@/components/model/FbxModel.vue'
import VrmModel from '@/components/model/VrmModel.vue'

export default {
  name: 'SystemRoleSettings',
  components: {
    FbxModel,
    VrmModel
  },
  data() {
    return {
      loading: false,
      roles: [],
      groups: [],
      query: { groupId: 1 },
      dialog: { visible: false, mode: 'create' },
      form: {
        id: undefined,
        name: '',
        prompt: '',
        speechRate: 1,
        pitchRate: 1,
        volume: 50,
        voice: '',
        language: '',
        groupId: undefined,
        isDefault: false,
        avatarPath: ''
      },
      uploadAction: '/api/laplace/file/upload',
      uploadModelAction: '/api/laplace/systemRole/uploadModel',
      modelUploadProgress: {}, // { roleId: progress }
      modelUploading: {}, // { roleId: boolean }
      loadedRoleModels: {} // { roleId: boolean } 手动点击后才加载的模型
    }
  },
  computed: {
    uploadHeaders() {
      const token = this.$store.state.token
      return token ? { Authorization: `Bearer ${token}` } : {}
    },
    hasAvatar() {
      const avatarPath = this.form.avatarPath
      return avatarPath && avatarPath.toString().trim() !== ''
    },
    dialogTitle() {
      return this.dialog.mode === 'edit' ? this.$t('systemRole.editRole') : this.$t('systemRole.addRole')
    },
    rules() {
      return {
        name: [{ required: true, message: '', trigger: 'blur' }],
        speechRate: [{ type: 'number', message: this.$t('systemRole.inputNumberError'), trigger: 'change' }],
        pitchRate: [{ type: 'number', message: this.$t('systemRole.inputNumberError'), trigger: 'change' }],
        volume: [{ type: 'number', message: this.$t('systemRole.inputNumberError'), trigger: 'change' }]
      }
    }
  },
  created() {
    this.fetchGroups()
  },
  methods: {
    formatNumber(n) {
      if (n === null || n === undefined || n === '') return '—'
      const num = Number(n)
      if (Number.isNaN(num)) return '—'
      return num.toFixed(1)
    },
    async fetchGroups() {
      try {
        const res = await api.getGroupList()
        if (res.statusCode === 100200) {
          this.groups = res.data || []
          // 如果没有设定过或设定无效，则根据列表设定默认值
          if (!this.query.groupId && this.groups.length > 0) {
            this.query.groupId = this.groups[0].id
          }
        }
        this.fetchData()
      } catch (error) {
        console.error(this.$t('systemRole.getGroupListFailed'), error)
        this.$message.error(this.$t('systemRole.getGroupListFailed'))
      }
    },
    async fetchData() {
      this.loading = true
      try {
        let res
        const params = {}
        if (this.query.groupId) {
          params.groupId = this.query.groupId
        }
        res = await api.listAll(params)
        const list = res.data?.records || res.data || []
        this.roles = res.data?.records ? list : list
      } finally {
        this.loading = false
      }
    },
    isRoleModelLoaded(roleId) {
      return !!this.loadedRoleModels[roleId]
    },
    loadRoleModelPreview(roleId) {
      this.$set(this.loadedRoleModels, roleId, true)
    },
    openCreate() {
      this.dialog.mode = 'create'
      this.dialog.visible = true
      this.form = {
        id: undefined,
        name: '',
        prompt: '',
        speechRate: 1,
        pitchRate: 1,
        volume: 50,
        voice: '',
        language: '',
        groupId: undefined,
        isDefault: false,
        avatarPath: ''
      }
      this.$nextTick(() => this.$refs.formRef?.clearValidate())
    },
    openEdit(row) {
      this.dialog.mode = 'edit'
      this.dialog.visible = true
      this.form = {
        ...row,
        avatarPath: row.avatarPath || '',
        groupId: row.groupId !== undefined && row.groupId !== null ? row.groupId : undefined
      }
      this.$nextTick(() => this.$refs.formRef?.clearValidate())
    },
    onSubmit() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        const payload = { ...this.form }
        if (!payload.avatarPath) {
          delete payload.avatarPath
        }
        try {
          if (this.dialog.mode === 'create') {
            await api.create(payload)
          } else {
            await api.update(payload)
          }
          this.$message.success(this.$t('systemRole.saveSuccess'))
          this.dialog.visible = false
          this.fetchData()
        } catch (error) {
          this.$message.error(this.$t('systemRole.saveFailed') + ': ' + (error.message || this.$t('systemRole.unknownError')))
        }
      })
    },
    onDelete(row) {
      this.$confirm(this.$t('systemRole.confirmDelete'), this.$t('systemRole.confirmDeleteTitle'), { type: 'warning' })
        .then(async () => {
          await api.remove({ id: row.id })
          this.$message.success(this.$t('systemRole.deleteSuccess'))
          this.fetchData()
        })
        .catch(() => {})
    },
    async onSetDefault(row) {
      await api.setDefault({ id: row.id })
      this.$message.success(this.$t('systemRole.setDefaultSuccess'))
      this.fetchData()
    },
    beforeModelUpload(role, file) {
      const isValidType = ['.fbx', '.vrm'].some(ext => 
        file.name.toLowerCase().endsWith(ext)
      )
      if (!isValidType) {
        this.$message.error(this.$t('systemRole.selectModelFile'))
        return false
      }
      this.$set(this.modelUploading, role.id, true)
      this.$set(this.modelUploadProgress, role.id, 0)
      return true
    },
    handleModelUploadProgress(role, event) {
      const progress = Math.round((event.loaded / event.total) * 100)
      this.$set(this.modelUploadProgress, role.id, progress)
    },
    handleModelUploadSuccess(role, res) {
      this.$set(this.modelUploading, role.id, false)
      if (res.statusCode === 100200) {
        this.$message.success(this.$t('systemRole.modelUploadSuccess'))
        setTimeout(() => {
          this.$set(this.modelUploadProgress, role.id, 0)
        }, 1000)
        this.fetchData()
      } else {
        this.$message.error(res.message || this.$t('systemRole.modelUploadFailed'))
        this.$set(this.modelUploadProgress, role.id, 0)
      }
    },
    handleModelUploadError(role, err) {
      this.$set(this.modelUploading, role.id, false)
      this.$set(this.modelUploadProgress, role.id, 0)
      this.$message.error(this.$t('systemRole.modelUploadFailed') + ': ' + (err.message || this.$t('systemRole.unknownError')))
    },
    getModelUploading(roleId) {
      return this.modelUploading[roleId] || false
    },
    getModelUploadProgress(roleId) {
      return this.modelUploadProgress[roleId] || 0
    },
    beforeAvatarUpload(file) {
      const isValidType = file.type === 'image/jpeg' || file.type === 'image/png'
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isValidType) {
        this.$message.error(this.$t('systemRole.avatarFormatError'))
      }
      if (!isLt2M) {
        this.$message.error(this.$t('systemRole.avatarSizeError'))
      }
      return isValidType && isLt2M
    },
    handleAvatarSuccess(res) {
      if (res.statusCode === 100200) {
        const avatarUrl = res.data?.url || res.data
        this.$set(this.form, 'avatarPath', avatarUrl)
        this.$message.success(this.$t('systemRole.avatarUploadSuccess'))
      } else {
        this.$message.error(res.message || this.$t('systemRole.avatarUploadFailed'))
      }
    },
    handleImageError() {
      this.$message.warning(this.$t('systemRole.avatarLoadFailed'))
      this.$set(this.form, 'avatarPath', '')
    },
    // 统一判断模型格式（优先后端的 format 字段，没有则从路径解析）
    getModelFormat(role) {
      if (role.format) {
        return String(role.format).toLowerCase()
      }
      const path = role.modelPath || ''
      const ext = path.split('.').pop()
      return ext ? ext.toLowerCase() : ''
    },
    onLoadProgress(progress) {
      console.log(`Model loading progress: ${(progress * 100).toFixed(2)}%`)
    },
    onLoadError(error) {
      console.error(this.$t('systemRole.modelLoadFailed'), error)
      this.$message.error(this.$t('systemRole.modelLoadFailed'))
    }
  }
}
</script>

<style scoped>
.system-roles-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px;
  padding-top: 0px;
  background-color: #232a3e;
  color: #fff;
}

.page-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.line-clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  line-clamp: 5;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  min-height: 7.5em;
}
.prompt-tooltip {
  max-width: 100px;
  white-space: normal;
  word-break: break-word;
}

.avatar-upload-container {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-uploader:hover {
  border-color: #409eff;
}

.avatar-preview {
  position: relative;
  width: 100px;
  height: 100px;
}

.avatar-preview:hover .avatar-overlay {
  opacity: 1;
}

.avatar-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.avatar-overlay i {
  color: white;
  font-size: 20px;
}

.avatar-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  margin-bottom: 4px;
}

.upload-text {
  font-size: 12px;
  color: #8c939d;
}

.avatar {
  width: 100px;
  height: 100px;
  object-fit: cover;
  display: block;
}

.avatar-tips {
  color: #999;
  font-size: 12px;
  line-height: 1.4;
}

.avatar-tips p {
  margin: 0 0 4px;
}

.upload-model-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
}

.model-preview-container {
  position: relative;
}

.reupload-button-container {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.group-radio-group {
  background-color: transparent;
}

.group-radio-group ::v-deep .el-radio-button__inner {
  background-color: #374151;
  border-color: #4b5563;
  color: #d1d5db;
}

.group-radio-group ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
  background-color: #7c3aed;
  border-color: #7c3aed;
  color: #ffffff;
  box-shadow: -1px 0 0 0 #7c3aed;
}

.group-radio-group ::v-deep .el-radio-button:first-child .el-radio-button__inner {
  border-left: 1px solid #4b5563;
}

.group-radio-group ::v-deep .el-radio-button__inner:hover {
  color: #7c3aed;
}
</style>

